import { defineStore } from 'pinia';
import { carAdvisoryService } from '../../apiCarAdvisoryService';
import { ICarAvailableStoreState } from '../../interfaces/ICarAvailableStoreState';
import { IStateModelData } from '../../interfaces/IStateModelData';
import { useCarSelectStore } from '../carSelectStore';
import { hasStoreContentExpired } from '../../helpers';
import { MAX_ALLOWED_TO_SEARCH, YEAR_FILTER } from '../../appConstants';
import { IVehicleSearchGroup, IVehicleSearchGroupAms } from '../../interfaces/IVehicleSearchGroup';
import { ISearchResultData } from '../../interfaces/ISearchResultData';
import { IVehicleGroup, IVehicleGroupAms } from '../../interfaces/IVehicleGroup';
import { IVariant, IVariantAms } from '../../interfaces/IVariant';

export const useCarAvailableStore = defineStore('carAvailableStore', {
  state: (): ICarAvailableStoreState => ({
    // totalCountResultsPage: 0,
    totalCount: 0,
    availableMakes: [],
    availableModels: [],
    availableBadges: [],
    searchResults: {
      vehicleSearchGroupResults: [],
      totalCount: 0,
    } as ISearchResultData,
    storeLastRefreshed: '', //the datetime when store has been last reset - cache cleared
  }),
  actions: {
    /**
     * Adds the make of a car and models, for the given make, to the store.
     *
     * @param make The string representing the make of the car
     */
    addMakeAndRelatedModels(make: string) {
      const carAdvisoryStore = useCarSelectStore();
      this.$patch((state: ICarAvailableStoreState) => {
        if (!carAdvisoryStore.selectedMakes.includes(make)) carAdvisoryStore.selectedMakes.push(make);
        this.addMakeRelatedModels(make);
      });
    },

    /**
     * Adds the models of cars for the given make.
     *
     * @param make The string representing the make of the car
     * @returns
     */
    async addMakeRelatedModels(make: string) {
      if (!make || this.availableModels.find((model: IStateModelData) => model.makeName === make)) {
        return;
      }

      const response = await carAdvisoryService.fetchModelsByMake(make, '');

      if (response && response.models) {
        // filter out duplicates
        const finalModelList: any[] = [];
        let found: any = false;

        if (response.models.length > 0) {
          // for each model
          response.models.forEach((newModel: any) => {
            let foundInFinal = false;

            // check if the model is already in the store's list
            found = this.availableModels.find((availableModelItem: IStateModelData) => {
              return availableModelItem.makeName === newModel.model.make && availableModelItem.modelName === newModel.model.name;
            });

            // check if the model is in the cumulative list of models that we need to add
            foundInFinal = finalModelList.find((finalModelItem) => {
              return finalModelItem.makeName === newModel.model.make && finalModelItem.modelName === newModel.model.name;
            });

            // if not found then add it to the cumulative list of models to add
            if (!found && !foundInFinal) {
              finalModelList.push({ makeName: newModel.model.make, modelName: newModel.model.name });
            }
          });

          // append the cumulative list of models to the store
          if (finalModelList.length > 0) {
            this.availableModels = this.availableModels.concat(finalModelList);
          }
        }
      }
    },
    resetStoreCache(numberOfHoursForStoreContentToExpire: number) {
      if (this.storeLastRefreshed) {
        //if last updated date is set in the store
        const hasContentExpired = hasStoreContentExpired(this.storeLastRefreshed, numberOfHoursForStoreContentToExpire);
        if (hasContentExpired) {
          //  if last updated time + config hours in less than current system datetime - then store require updating
          console.log('Store content refreshed => Car Comparison store');
          this.$reset();
          this.storeLastRefreshed = new Date().toString();
        }
      } else {
        console.log('Setting last updated on store => Car Comparison store');
        this.storeLastRefreshed = new Date().toString();
      }
    },
    /**
     * Add badges of cars for the make, model combination to the store
     *
     * @param model The object containing the make and model
     * @returns
     */
    async addMakeModelRelatedBadges(model: IStateModelData) {
      if (!model) {
        return;
      }

      // check if it's already in the store
      const badgeListFound =
        this.availableBadges.findIndex((badgeItem: any) => badgeItem.makeName === model.makeName && badgeItem.modelName === model.modelName) >= 0;

      if (badgeListFound) {
        return;
      }

      // Add badges for the model
      const childItems = [
        {
          op: 'and',
          children: [
            { field: 'vehicle_make', eq: [model.makeName.toLowerCase()] },
            { field: 'vehicle_model', eq: [model.modelName.toLowerCase()] },
          ],
        },
      ];

      const requestBody = {
        filter: childItems[0],
        includeTotalCount: true,
        fields: [
          'vehicle_uid',
          'vehicle_make',
          'vehicle_model',
          'search_text',
          'vehicle_trim',
          'vehicle_trim_correct_case',
          'vehicle_make_correct_case',
          'vehicle_model_correct_case',
        ],
      };

      const response = await carAdvisoryService.fetchVehiclesBySearch(requestBody);

      if (response && response.results && response.results.length > 0) {
        const badgeListToAdd: any[] = [];

        // for each badge
        response.results.forEach((newBadge: any) => {
          let foundInFinal = false;

          // check if it's in the cumulative list of badges to add
          foundInFinal = badgeListToAdd.find((finalBadgeItem) => {
            return (
              finalBadgeItem.makeName === newBadge.document.vehicle_make_correct_case &&
              finalBadgeItem.modelName === newBadge.document.vehicle_model_correct_case &&
              finalBadgeItem.badgeName === newBadge.document.vehicle_trim_correct_case
            );
          });

          // if not found in the cumulative list add it to the cumulative list
          if (!foundInFinal) {
            badgeListToAdd.push({
              makeName: newBadge.document.vehicle_make_correct_case,
              modelName: newBadge.document.vehicle_model_correct_case,
              badgeName: newBadge.document.vehicle_trim_correct_case,
            });
          }
        });

        // update the store with the cumulative list of badges
        this.availableBadges = this.availableBadges.concat(badgeListToAdd);
      }
    },
    /**
     * Get variants for the vehicle - based on the vehicleUid
     * @param vehicleUid The uid of the vehicle to retrieve.
     */
    async getVehicleGroupByVehicleUid(vehicleUid: string): Promise<IVehicleGroup | null> {
      // TODO: Move this method into CarGroupStore and reuse mapping functionality
      const requestBody = {
        filter: {
          op: 'and',
          children: [
            {
              field: 'vehicle_uid',
              eq: [vehicleUid],
            },
          ],
        },
        count: 1, // Only return a single result because we're using vehicle_uid as the lookup
      };

      // Request the trimStatusId (e.g. toyota-yaris-zr-current)
      const searchGroupResponse = await carAdvisoryService.fetchVehiclesBySearchGrouped(requestBody);
      if (!searchGroupResponse?.groupedResults?.length) {
        // Nothing was returned from the search so we can't retrieve variants
        return null;
      }
      const vehicleSearchGroupResponse = searchGroupResponse.groupedResults as IVehicleSearchGroupAms[];

      // Request the VehicleGroups by the TrimStatusId and filterContext
      const trimStatusId = vehicleSearchGroupResponse[0].trim_status_id;

      // Pass an empty filterContext so we return all variants for the trimStatusId - this may contain variants that we don't want to show
      // e.g. year 2004, etc.
      const vehicleGroupResponse = await carAdvisoryService.fetchVehicleGroupByTrimId(trimStatusId, '');
      if (!vehicleGroupResponse?.results?.length) {
        // Nothing was returned for the vehicleGroup
        return null;
      }
      const mappedResponse = vehicleGroupResponse?.results[0].document as IVehicleGroupAms;

      // Remove the unwanted variants based on year
      const yearFilteredVehicleGroup = mappedResponse.variants.filter((x) => {
        return (x.year_high && x.year_high >= YEAR_FILTER.minYearAllowed) || (x.year_low && x.year_low >= YEAR_FILTER.minYearAllowed);
      });
      mappedResponse.variants = yearFilteredVehicleGroup;

      const vehicleGroup = {
        trimStatusId: mappedResponse.trim_status_id,
        vehicleTrim: mappedResponse.vehicle_trim_correct_case,
        vehicleStatus: mappedResponse.vehicle_status_correct_case,
        vehicleModel: mappedResponse.vehicle_model_correct_case,
        vehicleMake: mappedResponse.vehicle_make_correct_case,
        variants: mappedResponse.variants.map(
          (variantItem: IVariantAms) =>
            ({
              id: variantItem.vehicle_uid,
              yearLow: variantItem.year_low,
              yearHigh: variantItem.year_high,
              heroImage: variantItem.hero_image_url,
              badgeDetails: variantItem.vehicle_version_correct_case,
              emissionsRating: variantItem.environment_score,
              drivenWheels: variantItem.driven_wheels,
              vehicleSeats: variantItem.vehicle_seats,
              vehicleDoors: variantItem.vehicle_doors,
              overallPowertrain: variantItem.overall_powertrain,
              fuelType: variantItem.fuel_type,
              fuelScore: variantItem.fuel_score,
              retailPrice: variantItem.retail_price,
              status: mappedResponse.vehicle_status_correct_case,
              // No need to call calculateRacqPrice() because we're not sorting here
              racqPrice:
                mappedResponse.vehicle_status_correct_case?.toLowerCase() === 'current'
                  ? variantItem.qld_driveawayprice
                  : variantItem.price_current_retail_value_average || 0,
              drivingRange: variantItem.driving_range_electric_motor_only_km,
              ancapScore: variantItem.ancap_overall_score,
              powertrain: variantItem.powertrain, //NOTE: powertrain field name is all lower case.
              fuelEconomy: variantItem.engine_fuel_consumption_combined_adr8102_lt_100km,
              powerConsumption: variantItem.power_consumption_combined_kwh_100km,
              fuelCostsPerMonth: variantItem.qld_fuel_cost_monthly,
              fuelOctane: variantItem.primary_engine_motor_fuel_octane,
              averageUsedRetailPrice: variantItem.price_current_retail_value_average,
              qldDriveAwayPrice: variantItem.qld_driveawayprice,
              primaryMotorFuelType: variantItem.primary_engine_motor_fuel_type,
              yearLowHigh: variantItem.year_low && variantItem.year_high ? `${variantItem.year_low}-${variantItem.year_high}` : undefined,
            } as IVariant)
        ),
      } as IVehicleGroup;

      return vehicleGroup;
    },
    getSearchDataForAnalytics() {
      const selectedStore = useCarSelectStore();
      return selectedStore.getFiltersForAnalytics;
    },
    /**
     * search vehicles
     */
    async performSearch(): Promise<IVehicleSearchGroup[]> {
      const selectedStore = useCarSelectStore();
      const criteria = selectedStore.getSearchCriteria();
      const sortOrder = selectedStore.getSortOrder();

      // only search if we have some criteria for the query
      const requestBody = {
        filter: criteria,
        count: MAX_ALLOWED_TO_SEARCH,
        orderBy: sortOrder[0].value,
      };

      try {
        const response = await carAdvisoryService.fetchVehiclesBySearchGrouped(requestBody);
        if (response.groupedResults && response.groupedResults.length > 0) {
          const vehicleSearchGroupResponse = response.groupedResults as IVehicleSearchGroupAms[];

          return vehicleSearchGroupResponse.map((resultItem: IVehicleSearchGroupAms) => ({
            trimStatusId: resultItem.trim_status_id,
            makeName: resultItem.vehicle_make_correct_case,
            makeNameLowerCase: resultItem.vehicle_make_correct_case?.toLowerCase(),
            modelName: resultItem.vehicle_model_correct_case,
            modelNameLowerCase: resultItem.vehicle_model_correct_case?.toLowerCase(),
            badgeName: resultItem.vehicle_trim_correct_case,
            status: resultItem.vehicle_status_correct_case,
            yearHigh: resultItem.group_year_high,
            yearLow: resultItem.group_year_low,
            priceHigh: resultItem.group_qld_price_high,
            priceLow: resultItem.group_qld_price_low,
            emissionsScoreHigh: resultItem.group_environment_score_high,
            emissionsScoreLow: resultItem.group_environment_score_low,
            fuelScoreHigh: resultItem.group_fuel_score_high,
            fuelScoreLow: resultItem.group_fuel_score_low,
            filterContext: response.filterContext,
          })) as IVehicleSearchGroup[];
        }
      } catch (el) {
        console.warn('Search could not be performed. Please check your search criteria.', el);
      }

      return [];
    },
    /**
     * Perform a search to get the count of vehicles based on the search criteria
     */
    async doSearch() {
      const selectedStore = useCarSelectStore();
      const criteria = selectedStore.getSearchCriteria();
      const sortOrder = selectedStore.getSortOrder();
      if (criteria.children?.length > 1) {
        // only search if we have some criteria for the query
        const requestBody = {
          filter: criteria,
          count: 1, // Don't care about returning the actual search results, we're only after the totalCount
          orderBy: sortOrder[0].value,
        };

        const response = await carAdvisoryService.fetchVehiclesBySearchGrouped(requestBody);

        if (response) {
          this.totalCount = response.totalCount;
        }
      } else {
        // reset the totalCount to avoid displaying a stale count value in the Search button
        this.totalCount = 0;
      }
    },
  },

  persist: true,
});
